import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

/**
 * Button — minimal accessible base button.
 *
 * Designed to work in both browser and Capacitor WebView (no DOM-only quirks).
 * Use Tailwind utilities for variant overrides; this component provides safe
 * defaults (focus-visible ring, disabled states, semantic typography).
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual variant — maps to brand tokens defined in @draftedge/ui's Tailwind preset. */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  /** Size token. */
  size?: 'sm' | 'md' | 'lg';
  /** Full width button (mobile-friendly default). */
  block?: boolean;
  children: ReactNode;
}

const VARIANT_CLASSES: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary:
    'bg-brand-primary text-white hover:bg-brand-primary-600 active:bg-brand-primary-700',
  secondary:
    'bg-brand-secondary text-white hover:bg-brand-secondary-600 active:bg-brand-secondary-700',
  ghost:
    'bg-transparent text-brand-primary hover:bg-brand-primary/10 active:bg-brand-primary/20',
  danger:
    'bg-brand-danger text-white hover:bg-brand-danger-600 active:bg-brand-danger-700',
};

const SIZE_CLASSES: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      block = false,
      className = '',
      type = 'button',
      children,
      ...rest
    },
    ref,
  ) => {
    const base =
      'inline-flex items-center justify-center gap-2 rounded-md font-semibold tracking-tight ' +
      'transition-colors duration-150 ease-out ' +
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 ' +
      'disabled:opacity-50 disabled:cursor-not-allowed select-none';

    const composed = [
      base,
      VARIANT_CLASSES[variant],
      SIZE_CLASSES[size],
      block ? 'w-full' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button ref={ref} type={type} className={composed} {...rest}>
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
